import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useColors } from "../constants/Colors";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { trpcReact, queryClient, trpcClient } from "../trpc_client";
import {
  Application,
  ApplicationLike,
} from "../components/ApplicationGridItem";
import {
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  Play,
  Pause,
  Volume2,
  VolumeX,
  MessageSquare,
  Video,
  Heart,
} from "lucide-react";
import Hls, { ErrorData } from "hls.js";

interface CarouselItem extends Application {
  type: "video" | "text";
}

interface LikerProfile {
  user_id: number;
  username: string;
  profile_image: string | null;
}

const CarouselScreen: React.FC = () => {
  const { t } = useTranslation();
  const colors = useColors();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getIdToken } = useAuth();
  const { userExistsInDb, profile } = useProfile();

  // Get initial state from navigation
  const locationState = location.state as {
    applications?: CarouselItem[];
    initialIndex?: number;
    type?: "sent" | "received";
  } | null;

  const [currentIndex, setCurrentIndex] = useState(
    locationState?.initialIndex || 0,
  );
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [localLikes, setLocalLikes] = useState<
    Record<number, ApplicationLike[]>
  >({});
  const [likerProfiles, setLikerProfiles] = useState<
    Record<number, LikerProfile>
  >({});

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const hlsInstancesRef = useRef<Map<number, Hls>>(new Map());
  const authTokenRef = useRef<string | null>(null);

  const applicationType = locationState?.type || "sent";

  // Fetch applications if not provided via navigation state
  const { data: sentApplications, refetch: refetchSentApplications } =
    trpcReact.getApplicationsByUser.useQuery(undefined, {
      enabled: userExistsInDb && !locationState?.applications,
    });

  const { data: flatProfilesData } =
    trpcReact.getFlatProfilesOfAppliedFlats.useQuery(undefined, {
      enabled:
        userExistsInDb &&
        applicationType === "sent" &&
        !locationState?.applications,
    });

  const { data: receivedApplications, refetch: refetchReceivedApplications } =
    trpcReact.getRoomApplications.useQuery(undefined, {
      enabled:
        userExistsInDb &&
        applicationType === "received" &&
        !locationState?.applications,
    });

  const { data: applicantProfilesData } =
    trpcReact.getApplicantProfiles.useQuery(undefined, {
      enabled:
        userExistsInDb &&
        applicationType === "received" &&
        !locationState?.applications,
    });

  // Like mutation
  const likeApplicationMutation = trpcReact.likeApplication.useMutation({
    onMutate: async ({ application_id }) => {
      // Optimistic update
      const currentUserId = profile?.user_id;
      if (!currentUserId) return;

      setLocalLikes((prev) => {
        const currentLikes =
          prev[application_id] ||
          carouselData.find((a) => a.application_id === application_id)
            ?.application_likes ||
          [];
        const isLiked = currentLikes.some(
          (like) => like.user_id === currentUserId,
        );

        if (isLiked) {
          // Remove like
          return {
            ...prev,
            [application_id]: currentLikes.filter(
              (like) => like.user_id !== currentUserId,
            ),
          };
        } else {
          // Add like
          return {
            ...prev,
            [application_id]: [
              ...currentLikes,
              { application_id, user_id: currentUserId },
            ],
          };
        }
      });
    },
    onError: (error, { application_id }) => {
      // Revert optimistic update on error
      console.error("Like error:", error);
      setLocalLikes((prev) => {
        const newState = { ...prev };
        delete newState[application_id];
        return newState;
      });
    },
  });

  const retractApplicationMutation = trpcReact.retractApplication.useMutation({
    onSuccess: () => {
      refetchSentApplications();
      refetchReceivedApplications();
    },
  });

  const declineApplicationMutation = trpcReact.declineApplication.useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [["getApplicationsByUser"]],
      });
      void queryClient.invalidateQueries({
        queryKey: [["getRoomApplications"]],
      });
      navigate("/applications", { replace: true });
    },
  });

  // Create profile maps
  const flatProfilesMap = useMemo(() => {
    if (!flatProfilesData?.flatProfiles) return {};
    const map: Record<
      number,
      { flat_id: number; name: string; image: string | null }
    > = {};
    for (const profile of flatProfilesData.flatProfiles) {
      if (profile.application_id && profile.flat) {
        map[profile.application_id] = {
          flat_id: profile.flat.flat_id,
          name: profile.flat.name || "Unknown Flat",
          image: profile.urls?.[0] || null,
        };
      }
    }
    return map;
  }, [flatProfilesData]);

  const applicantProfilesMap = useMemo(() => {
    if (!applicantProfilesData) return {};
    const map: Record<number, { name: string; image: string | null }> = {};
    for (const profile of applicantProfilesData) {
      if (profile.application_id) {
        map[profile.application_id] = {
          name: profile.username || "Unknown",
          image: profile.profile_images?.[0] || null,
        };
      }
    }
    return map;
  }, [applicantProfilesData]);

  // Build carousel data
  const carouselData: CarouselItem[] = useMemo(() => {
    if (locationState?.applications) {
      return locationState.applications;
    }

    if (applicationType === "sent" && sentApplications) {
      return (sentApplications as Application[])
        .filter((app) => app.state !== "declined" && app.state !== "retracted")
        .map((app) => {
          const flatProfile = flatProfilesMap[app.application_id];
          return {
            ...app,
            flat_id: flatProfile?.flat_id,
            flat_name: flatProfile?.name,
            flat_image: flatProfile?.image,
          } as CarouselItem;
        });
    }

    if (applicationType === "received" && receivedApplications) {
      return receivedApplications
        .flatMap((room) => room.applications)
        .filter((app) => app.state !== "declined" && app.state !== "retracted")
        .map((app) => {
          const applicantProfile = applicantProfilesMap[app.application_id];
          return {
            ...app,
            applicant_name: applicantProfile?.name,
            applicant_image: applicantProfile?.image,
          } as CarouselItem;
        });
    }

    return [];
  }, [
    locationState?.applications,
    applicationType,
    sentApplications,
    receivedApplications,
    flatProfilesMap,
    applicantProfilesMap,
  ]);

  const currentItem = carouselData[currentIndex];

  // Get likes for an application (with optimistic updates)
  const getLikes = useCallback(
    (item: CarouselItem) => {
      return localLikes[item.application_id] ?? item.application_likes ?? [];
    },
    [localLikes],
  );

  // Check if current user has liked
  const hasUserLiked = useCallback(
    (item: CarouselItem) => {
      const likes = getLikes(item);
      return likes.some((like) => like.user_id === profile?.user_id);
    },
    [getLikes, profile?.user_id],
  );

  // Handle like toggle
  const handleLike = useCallback(
    (item: CarouselItem) => {
      if (!profile?.user_id) return;
      likeApplicationMutation.mutate({ application_id: item.application_id });
    },
    [likeApplicationMutation, profile?.user_id],
  );

  const canRetract = useCallback(
    (item: CarouselItem) =>
      !["accepted", "declined", "retracted"].includes(item.state),
    [],
  );

  const canDecline = useCallback(
    (item: CarouselItem) =>
      !["accepted", "declined", "retracted"].includes(item.state),
    [],
  );

  // Fetch auth token
  useEffect(() => {
    const fetchToken = async () => {
      if (user) {
        const token = await getIdToken();
        setAuthToken(token);
        authTokenRef.current = token;
      }
    };
    fetchToken();
  }, [user, getIdToken]);

  // Scroll to initial index on mount
  useEffect(() => {
    if (scrollContainerRef.current && locationState?.initialIndex) {
      const container = scrollContainerRef.current;
      const scrollPosition =
        locationState.initialIndex * container.clientHeight;
      container.scrollTo({ top: scrollPosition, behavior: "instant" });
    }
  }, [locationState?.initialIndex]);

  // Handle scroll snap - detect current index from scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollTimeout: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollTop = container.scrollTop;
        const itemHeight = container.clientHeight;
        const newIndex = Math.round(scrollTop / itemHeight);
        if (
          newIndex !== currentIndex &&
          newIndex >= 0 &&
          newIndex < carouselData.length
        ) {
          setCurrentIndex(newIndex);
          setIsPlaying(true);
        }
      }, 50);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [currentIndex, carouselData.length]);

  // Setup video player for current item
  const setupVideoPlayer = useCallback(
    (video: HTMLVideoElement, item: CarouselItem, index: number) => {
      if (!item.url) return;

      const token = authTokenRef.current;
      if (!token) return;

      const videoUrl = item.url;
      const isHlsStream = videoUrl.includes(".m3u8");

      // Cleanup previous HLS instance for this index
      const existingHls = hlsInstancesRef.current.get(index);
      if (existingHls) {
        existingHls.destroy();
        hlsInstancesRef.current.delete(index);
      }

      if (isHlsStream && Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          xhrSetup: (xhr: XMLHttpRequest) => {
            const currentToken = authTokenRef.current;
            if (currentToken) {
              xhr.setRequestHeader("authorization", currentToken);
            }
          },
        });
        hlsInstancesRef.current.set(index, hls);

        hls.loadSource(videoUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (index === currentIndex && isPlaying) {
            video.play().catch(console.log);
          }
        });

        hls.on(Hls.Events.ERROR, (_event: string, data: ErrorData) => {
          console.error("HLS error:", data);
        });
      } else if (
        isHlsStream &&
        video.canPlayType("application/vnd.apple.mpegurl")
      ) {
        video.src = videoUrl;
      } else {
        video.src = videoUrl;
      }
    },
    [currentIndex, isPlaying],
  );

  // When auth token becomes available, initialize any video players that are waiting
  useEffect(() => {
    if (!authToken) return;
    videoRefs.current.forEach((video, index) => {
      const item = carouselData[index];
      if (item?.type === "video" && item.url && !hlsInstancesRef.current.has(index) && !video.src) {
        setupVideoPlayer(video, item, index);
      }
    });
  }, [authToken, carouselData, setupVideoPlayer]);

  // Manage video playback based on current index
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (index === currentIndex) {
        video.muted = isMuted;
        if (isPlaying) {
          video.play().catch(console.log);
        } else {
          video.pause();
        }
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [currentIndex, isPlaying, isMuted]);

  // Cleanup HLS instances on unmount
  useEffect(() => {
    return () => {
      hlsInstancesRef.current.forEach((hls) => hls.destroy());
      hlsInstancesRef.current.clear();
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        navigate(-1);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        goToNext();
      } else if (e.key === " ") {
        e.preventDefault();
        setIsPlaying((p) => !p);
      } else if (e.key === "m") {
        setIsMuted((m) => !m);
      } else if (e.key === "l" && currentItem) {
        handleLike(currentItem);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, carouselData.length, currentIndex, currentItem, handleLike]);

  const goToNext = useCallback(() => {
    if (currentIndex < carouselData.length - 1 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollTo({
        top: (currentIndex + 1) * container.clientHeight,
        behavior: "smooth",
      });
    }
  }, [currentIndex, carouselData.length]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollTo({
        top: (currentIndex - 1) * container.clientHeight,
        behavior: "smooth",
      });
    }
  }, [currentIndex]);

  const goToIndex = useCallback(
    (index: number) => {
      if (
        scrollContainerRef.current &&
        index >= 0 &&
        index < carouselData.length
      ) {
        const container = scrollContainerRef.current;
        container.scrollTo({
          top: index * container.clientHeight,
          behavior: "smooth",
        });
      }
    },
    [carouselData.length],
  );

  const getDisplayName = (item: CarouselItem) => {
    if (applicationType === "sent") {
      return item.flat_name || "Unknown Flat";
    }
    return item.applicant_name || "Unknown";
  };

  const getDisplayImage = (item: CarouselItem) => {
    if (applicationType === "sent") {
      return item.flat_image;
    }
    return item.applicant_image;
  };

  const handleProfileClick = (item: CarouselItem) => {
    if (applicationType === "sent" && item.flat_id) {
      navigate(`/app/flat-view/${item.flat_id}`);
    } else if (applicationType === "received" && item.user_id) {
      navigate(`/app/profile/${item.user_id}`);
    }
  };

  useEffect(() => {
    if (!currentItem) return;

    const likeUserIds = Array.from(
      new Set(getLikes(currentItem).map((like) => like.user_id)),
    );
    const missingUserIds = likeUserIds.filter(
      (userId) => userId !== profile?.user_id && !likerProfiles[userId],
    );

    if (!missingUserIds.length) return;

    let isCancelled = false;

    const loadLikerProfiles = async () => {
      const profiles = await Promise.all(
        missingUserIds.map(async (userId) => {
          try {
            const userProfile = await trpcClient.getUserProfile.query({
              user_id: userId,
            });
            return {
              user_id: userId,
              username: userProfile.username || "Unknown",
              profile_image: userProfile.profile_images?.[0] || null,
            } as LikerProfile;
          } catch (error) {
            console.error("Failed to load liker profile:", error);
            return {
              user_id: userId,
              username: "Unknown",
              profile_image: null,
            } as LikerProfile;
          }
        }),
      );

      if (isCancelled) return;

      setLikerProfiles((prev) => {
        const next = { ...prev };
        for (const likerProfile of profiles) {
          next[likerProfile.user_id] = likerProfile;
        }
        return next;
      });
    };

    void loadLikerProfiles();

    return () => {
      isCancelled = true;
    };
  }, [currentItem, getLikes, likerProfiles, profile?.user_id]);

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
        <p className="text-white">Please sign in to view applications.</p>
      </div>
    );
  }

  if (carouselData.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
        <p className="text-white mb-4">No applications to display.</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-3 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black overflow-hidden">
      {/* Header with gradient */}
      <div
        className="absolute top-0 left-0 right-0 z-20 px-4 pt-4 pb-16 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 100%)",
        }}
      >
        <div className="flex items-center gap-4 pointer-events-auto max-w-screen-xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors backdrop-blur-md"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          {/* Profile info */}
          {currentItem && (
            <button
              onClick={() => handleProfileClick(currentItem)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              {getDisplayImage(currentItem) ? (
                <img
                  src={getDisplayImage(currentItem)!}
                  alt={getDisplayName(currentItem)}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-semibold border-2 border-white/30"
                  style={{ backgroundColor: colors.accentColor }}
                >
                  {getDisplayName(currentItem).charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-white font-semibold text-lg drop-shadow-lg">
                {getDisplayName(currentItem)}
              </span>
            </button>
          )}

          {/* Application type badge */}
          <div className="ml-auto flex items-center gap-2">
            <div className="px-3 py-1 rounded-full bg-black/40 backdrop-blur-md flex items-center gap-2">
              {currentItem?.type === "video" ? (
                <Video className="w-4 h-4 text-white" />
              ) : (
                <MessageSquare className="w-4 h-4 text-white" />
              )}
              <span className="text-white text-sm">
                {currentIndex + 1} / {carouselData.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Vertical pagination on the left */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-20">
        {carouselData
          .slice(
            Math.max(0, currentIndex - 4),
            Math.min(carouselData.length, currentIndex + 5),
          )
          .map((_, i) => {
            const actualIndex = Math.max(0, currentIndex - 4) + i;
            return (
              <button
                key={actualIndex}
                onClick={() => goToIndex(actualIndex)}
                className={`rounded-full transition-all ${
                  actualIndex === currentIndex
                    ? "h-8 w-2 bg-white"
                    : "h-2 w-2 bg-white/40 hover:bg-white/60"
                }`}
              />
            );
          })}
      </div>

      {/* Vertical scroll container with snap */}
      <div
        ref={scrollContainerRef}
        className="w-full h-full flex flex-col overflow-y-auto overflow-x-hidden"
        style={{
          scrollSnapType: "y mandatory",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {carouselData.map((item, index) => (
          <div
            key={item.application_id}
            className="w-full h-full flex-shrink-0 flex items-center justify-center"
            style={{
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
              minHeight: "100%",
            }}
          >
            {item.type === "video" ? (
              // Video Application
              <div className="relative w-full h-full flex items-center justify-center">
                <video
                  ref={(el) => {
                    if (el) {
                      videoRefs.current.set(index, el);
                      if (item.url && !hlsInstancesRef.current.has(index) && !el.src) {
                        setupVideoPlayer(el, item, index);
                      }
                    }
                  }}
                  className="w-full h-full object-contain"
                  playsInline
                  loop
                  muted={isMuted}
                  onClick={() =>
                    index === currentIndex && setIsPlaying((p) => !p)
                  }
                />

                {/* Video controls overlay - only show for current item */}
                {index === currentIndex && (
                  <div className="absolute bottom-24 left-4 right-4 flex items-center justify-between pointer-events-none">
                    <div className="flex gap-3 pointer-events-auto">
                      <button
                        onClick={() => setIsPlaying((p) => !p)}
                        className="p-3 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors backdrop-blur-md"
                      >
                        {isPlaying ? (
                          <Pause className="w-6 h-6" />
                        ) : (
                          <Play className="w-6 h-6" />
                        )}
                      </button>
                      <button
                        onClick={() => setIsMuted((m) => !m)}
                        className="p-3 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors backdrop-blur-md"
                      >
                        {isMuted ? (
                          <VolumeX className="w-6 h-6" />
                        ) : (
                          <Volume2 className="w-6 h-6" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Liked users indicator bottom-center */}
                {index === currentIndex && getLikes(item).length > 0 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none">
                    {Array.from(
                      new Set(getLikes(item).map((like) => like.user_id)),
                    )
                      .slice(0, 4)
                      .map((userId) => {
                        const isCurrentUser = userId === profile?.user_id;
                        const imageUrl = isCurrentUser
                          ? profile?.profile_images?.[0] || null
                          : likerProfiles[userId]?.profile_image || null;
                        const displayName = isCurrentUser
                          ? profile?.username || "You"
                          : likerProfiles[userId]?.username || "User";

                        return (
                          <div
                            key={`liker-${item.application_id}-${userId}`}
                            className="relative w-11 h-11 rounded-full overflow-visible"
                            title={`${displayName} liked this`}
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={displayName}
                                className="w-11 h-11 rounded-full object-cover border-2 border-white/30"
                              />
                            ) : (
                              <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold border-2 border-white/30 bg-black/40 text-white">
                                {displayName.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center bg-violet-500 border-2 border-black">
                              <Heart className="w-2.5 h-2.5 text-white fill-white" />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            ) : (
              // Text Application - full-bleed image with solid card at bottom
              <div className="relative w-full h-full flex items-center justify-center">
                {/* 9:16 aspect ratio container */}
                <div
                  className="relative h-full max-h-full overflow-hidden flex flex-col justify-end"
                  style={{ aspectRatio: "9/16" }}
                >
                  {/* Background image */}
                  {getDisplayImage(item) ? (
                    <img
                      src={getDisplayImage(item)!}
                      alt={getDisplayName(item)}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className="absolute inset-0 w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: colors.accentColor }}
                    >
                      <span
                        className="text-9xl font-bold"
                        style={{ color: colors.textNeutralOnPrimary }}
                      >
                        {getDisplayName(item).charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}

                  {/* Solid card at bottom */}
                  <div
                    className="relative mx-4 mb-4 p-4 overflow-hidden"
                    style={{
                      borderRadius: 24,
                      backgroundColor: colors.containerNeutralSolidSurface,
                    }}
                  >
                    {/* Like button row */}
                    <div className="flex items-center justify-end mb-3">
                      {applicationType === "received" ? (
                        <button
                          onClick={() => handleLike(item)}
                          className="transition-transform duration-150 hover:scale-110 active:scale-95"
                        >
                          <Heart
                            className="w-6 h-6"
                            style={{ color: colors.textNeutralDefault }}
                            fill={
                              hasUserLiked(item)
                                ? colors.textNeutralDefault
                                : "none"
                            }
                          />
                        </button>
                      ) : getLikes(item).length > 0 ? (
                        <Heart
                          className="w-6 h-6"
                          style={{ color: colors.textNeutralDefault }}
                          fill={colors.textNeutralDefault}
                        />
                      ) : null}
                    </div>

                    {/* Application text */}
                    <p
                      className="text-[15px] leading-relaxed whitespace-pre-wrap max-h-[30vh] overflow-y-auto"
                      style={{ color: colors.textNeutralDefault }}
                    >
                      {item.text_application_answer || "No message provided."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Right side controls - Navigation and Like */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-20">
        <button
          onClick={goToPrevious}
          disabled={currentIndex === 0}
          className={`p-3 rounded-full bg-black/40 text-white transition-all backdrop-blur-md ${
            currentIndex === 0
              ? "opacity-30 cursor-not-allowed"
              : "hover:bg-black/60"
          }`}
        >
          <ChevronUp className="w-6 h-6" />
        </button>

        {/* Like button for video applications - received can interact, sent can only view */}
        {currentItem?.type === "video" && applicationType === "received" && (
          <button
            onClick={() => handleLike(currentItem)}
            className={`p-3 rounded-full transition-all backdrop-blur-md flex flex-col items-center ${
              hasUserLiked(currentItem)
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-black/40 text-white hover:bg-black/60"
            }`}
          >
            <Heart
              className={`w-6 h-6 ${hasUserLiked(currentItem) ? "fill-current" : ""}`}
            />
            {getLikes(currentItem).length > 0 && (
              <span className="text-xs mt-1 font-medium">
                {getLikes(currentItem).length}
              </span>
            )}
          </button>
        )}

        {/* Show like count for sent video applications (read-only) */}
        {currentItem?.type === "video" &&
          applicationType === "sent" &&
          getLikes(currentItem).length > 0 && (
            <div className="p-3 rounded-full bg-black/40 text-white backdrop-blur-md flex flex-col items-center">
              <Heart className="w-6 h-6 fill-red-500 text-red-500" />
              <span className="text-xs mt-1 font-medium">
                {getLikes(currentItem).length}
              </span>
            </div>
          )}

        {currentItem &&
          applicationType === "sent" &&
          canRetract(currentItem) && (
            <button
              onClick={() => {
                if (!window.confirm("Retract this application?")) return;
                retractApplicationMutation.mutate({
                  application_id: currentItem.application_id,
                });
              }}
              disabled={retractApplicationMutation.isPending}
              className="px-3 py-2 rounded-full bg-black/40 text-white transition-all backdrop-blur-md hover:bg-black/60 disabled:opacity-50"
            >
              {retractApplicationMutation.isPending ? "..." : "Retract"}
            </button>
          )}

        {currentItem &&
          applicationType === "received" &&
          canDecline(currentItem) && (
            <button
              onClick={() => {
                if (!window.confirm("Decline this application?")) return;
                declineApplicationMutation.mutate({
                  application_id: currentItem.application_id,
                });
              }}
              disabled={declineApplicationMutation.isPending}
              className="px-3 py-2 rounded-full bg-red-500 text-white transition-all hover:bg-red-600 disabled:opacity-50"
            >
              {declineApplicationMutation.isPending ? "..." : "Decline"}
            </button>
          )}

        <button
          onClick={goToNext}
          disabled={currentIndex === carouselData.length - 1}
          className={`p-3 rounded-full bg-black/40 text-white transition-all backdrop-blur-md ${
            currentIndex === carouselData.length - 1
              ? "opacity-30 cursor-not-allowed"
              : "hover:bg-black/60"
          }`}
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>

      {/* Keyboard hints (desktop only) */}
      <div className="hidden lg:block absolute bottom-4 right-4 z-20">
        <div className="flex gap-2 text-white/50 text-xs">
          <span className="px-2 py-1 bg-white/10 rounded">↑↓ Navigate</span>
          {currentItem?.type === "video" && (
            <>
              <span className="px-2 py-1 bg-white/10 rounded">
                Space Play/Pause
              </span>
              <span className="px-2 py-1 bg-white/10 rounded">M Mute</span>
            </>
          )}
          {applicationType === "received" && (
            <span className="px-2 py-1 bg-white/10 rounded">L Like</span>
          )}
          <span className="px-2 py-1 bg-white/10 rounded">Esc Close</span>
        </div>
      </div>
    </div>
  );
};

export default CarouselScreen;
